import { Injectable } from '@angular/core';
import { collection, doc, Firestore, getDoc, getDocs, limit, query, setDoc, updateDoc, where } from '@angular/fire/firestore';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { UserService } from './user.service';
import { UserRegistration } from '../../interfaces/master-interfaces';

/**
 * ✅ CRITICAL FIX v2: Never call UserService.getDeviceId()
 * 
 * The getDeviceId() method creates a separate device_id entry in IDB.
 * This causes dual entries:
 *   - device_id = "SilentWalrus155" (from UserService)
 *   - current_user.id = "SapphireArmadillo62" (from AnonymousUserService)
 * 
 * Solution: Only use UserService.getCurrentUser() to read/write current_user.
 * Never touch UserService device_id methods.
 */
@Injectable({
  providedIn: 'root'
})
export class AnonymousUserService {

  private _ready = new BehaviorSubject<boolean>(false);
  ready$ = this._ready.asObservable();

  currentUserId!: string;
  userDisplayName: string = '';
  private sharedNames: Map<string, string> = new Map();

  public currentUserSubject = new BehaviorSubject<{
    id: string,
    displayName: string,
    lat?: number | null;
    lon?: number | null
  } | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();

  private readonly USERS_COLLECTION = 'anonymous_users';

  constructor(
    private _firestore: Firestore,
    private _userService: UserService
  ) { }

  /**
  * ✅ Create new user (displayName as source of truth)
  * SINGLE entry: current_user with id === displayName
  * NO device_id entry created
  */
  async createNewUser(authUid?: string): Promise<{ id: string }> {
    try {
      // 1. Generate displayName for UI (user-facing friendly name)
      this.userDisplayName = this._userService.generateRandomUsername();

      // 2. ✅ Use authUid as userId (the SINGLE SOURCE OF TRUTH for document ID)
      // Falls back to displayName if no authUid provided
      this.currentUserId = authUid || this.userDisplayName;
      console.log('✅ currentUserId set to:', this.currentUserId);
      console.log('✅ userDisplayName set to:', this.userDisplayName);
      const { lat, lon } = this.generateMockGPSCoordinates();

      console.log('🆕 Creating new user');
      console.log('  userId (doc ID):', this.currentUserId);
      console.log('  displayName (UI):', this.userDisplayName);

      // 3. Save FULL user object to IndexedDB (SINGLE entry)
      const user = {
        id: this.currentUserId,           // ✅ authUid or displayName
        displayName: this.userDisplayName, // "MysteriousFox123" for UI
        lat: lat,
        lon: lon,
      };

      await this._userService.saveUser(user);
      console.log('✅ Saved to IDB: current_user (single entry)');

      // 4. Save to Firestore with auth.uid as document ID
      await this.saveUserToFirestore(lat, lon);
      console.log('✅ Saved to Firestore with doc ID:', this.currentUserId);

      // 5. Update subject
      this.currentUserSubject.next(user);

      console.log('✅ User created successfully:', this.currentUserId);
      return { id: this.currentUserId };

    } catch (error) {
      console.error('❌ Error creating new user:', error);
      throw error;
    }
  }

  /**
   * ✅ FIXED: Never rely on UserService.getDeviceId()
   * Only use getCurrentUser() which reads current_user without touching device_id
   */
  async getCurrentUserId(): Promise<string> {
    // Return cached ID if already loaded
    if (this.currentUserId) {
      return this.currentUserId;
    }

    try {
      // ✅ ONLY read current_user (doesn't create device_id)
      const existingUser = await this._userService.getCurrentUser();

      if (existingUser?.id) {
        // Load existing user
        this.currentUserId = existingUser.id;
        this.userDisplayName = existingUser.displayName;
        return this.currentUserId;
      }

      // No existing user → create new one
      const { id } = await this.createNewUser();
      return id;

    } catch (error) {
      console.error('❌ Error getting user ID:', error);
      return this.createFallbackUser();
    }
  }

  async getDisplayNameWithFallback(userId: string | undefined, fallback: string = 'User'): Promise<string> {
    if (!userId) {
      return fallback;
    }

    try {
      const userInfo = await this.getUserDisplayInfo(userId);
      return userInfo.displayName;
    } catch (error) {
      console.warn(`[getDisplayNameWithFallback] Failed to get display name for ${userId}:`, error);
      return fallback;
    }
  }

  async getUserDisplayInfo(userId: string): Promise<{ id: string, displayName: string }> {
    // 1. Check if it's the current user
    if (userId === this.currentUserId) {
      return { id: userId, displayName: this.userDisplayName };
    }

    // 2. Check if it's in shared names cache
    if (this.sharedNames.has(userId)) {
      const sharedName = this.sharedNames.get(userId)!;
      return { id: userId, displayName: sharedName };
    }

    // 3. Query Firestore anonymous_users collection
    try {
      const userData = await this.getUserById(userId);

      if (userData && userData.displayName) {
        // Cache the result in sharedNames for future use
        this.sharedNames.set(userId, userData.displayName);
        return { id: userId, displayName: userData.displayName };
      }
    } catch (firestoreError) {
      console.error(`[getUserDisplayInfo] Error querying Firestore for ${userId}:`, firestoreError);
    }

    throw new Error(`User not found: ${userId}`);
  }

  async getUserByDisplayName(displayName: string): Promise<UserRegistration | null> {
    const usersRef = collection(this._firestore, this.USERS_COLLECTION);
    const q = query(usersRef, where('displayName', '==', displayName), limit(1));

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    return snapshot.docs[0].data() as UserRegistration;
  }

  async getUserById(userId: string): Promise<any> {
    try {
      const userDocRef = doc(this._firestore, this.USERS_COLLECTION, userId);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData;
      } else {
        return null;
      }
    } catch (error) {
      console.error(`[getUserById] Error querying Firestore:`, error);
      throw error;
    }
  }



  async updateDisplayName(newDisplayName: string): Promise<void> {
    try {
      // Input validation
      if (!newDisplayName || newDisplayName.trim() === '') {
        throw new Error('Display name cannot be empty');
      }

      const trimmedDisplayName = newDisplayName.trim();

      // Display name is now the immutable ID
      this.userDisplayName = trimmedDisplayName;

      // Update in Firestore
      await this.updateUserDisplayNameInFirestore(this.currentUserId, trimmedDisplayName);

      // Publish updated user to subscribers
      this.currentUserSubject.next({
        id: this.currentUserId,
        displayName: trimmedDisplayName,
        lat: null,
        lon: null
      });

      console.log(`✅ Display name updated successfully to: ${trimmedDisplayName}`);
    } catch (error) {
      console.error('❌ Error updating display name:', error);
      throw error;
    }
  }

  private async updateUserDisplayNameInFirestore(userId: string, displayName: string): Promise<void> {
    try {
      const userDocRef = doc(this._firestore, this.USERS_COLLECTION, userId);
      await updateDoc(userDocRef, {
        displayName: displayName
      });
      console.log(`🔥 Display name synced to Firestore for user: ${userId}`);
    } catch (error) {
      console.error('❌ Failed to update display name in Firestore:', error);
      throw error;
    }
  }

  private generateMockGPSCoordinates(): { lat: number; lon: number; accuracy: number } {
    const lat = 40.7128 + (Math.random() - 0.5) * 0.1;
    const lon = -74.0060 + (Math.random() - 0.5) * 0.1;

    return {
      lat: parseFloat(lat.toFixed(6)),
      lon: parseFloat(lon.toFixed(6)),
      accuracy: 10 + Math.random() * 100
    };
  }

  async saveUserToFirestore(lat?: number, lon?: number): Promise<void> {
    try {
      // Use displayName as the Firestore doc ID
      const userDocRef = doc(this._firestore, this.USERS_COLLECTION, this.currentUserId!);

      const userData: UserRegistration = {
        userId: this.currentUserId!,
        displayName: this.userDisplayName,
        fcmToken: null,
        isUserPresent: true,
        isTabActive: true,
        lastSeen: Date.now(),
        createdAt: Date.now(),
        lat: lat || null,
        lon: lon || null,
      };

      await setDoc(userDocRef, userData, { merge: true });
      console.log('🔥 Firestore doc created with ID:', this.currentUserId);
    } catch (error) {
      console.error('❌ Error saving user to Firestore:', error);
    }
  }

  /**
   * ✅ Fallback uses displayName-based ID
   */
  createFallbackUser(): string {
    const fallbackName = this._userService.generateRandomUsername();

    this.currentUserId = fallbackName;
    this.userDisplayName = fallbackName;

    this.currentUserSubject.next({
      id: fallbackName,
      displayName: fallbackName,
    });

    console.warn(`🆘 Created fallback user: ${fallbackName}`);
    return fallbackName;
  }
}